import {
    ActivityIcon,
    ClipboardCheckIcon,
    DatabaseIcon,
    DollarSignIcon,
    Loader2Icon,
} from "lucide-react";

import {Badge} from "@components/ui/badge";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {
    useVNextAssumptions,
    useVNextOps,
    useVNextSummary,
    useVNextSyncStatus,
} from "@hooks/use-vnext";

interface SummaryTabProps {
    workspaceId: string;
    scenarioId: string;
    forecastMonths?: number;
}

function formatCurrency(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
}

function formatRelative(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

// ── Data Freshness Card ──────────────────────────────────────

function DataFreshnessCard() {
    const {syncStatus, isLoading} = useVNextSyncStatus();

    if (isLoading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DatabaseIcon className="size-4 text-[#209DB3]" />
                        Data Freshness
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    const hasSynced = syncStatus?.has_synced;
    const status = syncStatus?.status;
    const isStale = syncStatus?.is_stale;

    const badgeVariant = "outline" as const;
    let badgeClass = "border-slate-300 text-slate-500";
    let badgeLabel = "Never synced";

    if (hasSynced) {
        if (status === "error") {
            badgeClass = "border-red-400 text-red-600";
            badgeLabel = "Error";
        } else if (status === "running") {
            badgeClass = "border-blue-400 text-blue-600";
            badgeLabel = "Syncing";
        } else if (isStale || status === "partial") {
            badgeClass = "border-amber-400 text-amber-600";
            badgeLabel = "Stale";
        } else {
            badgeClass = "border-green-400 text-green-600";
            badgeLabel = "Fresh";
        }
    }

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <DatabaseIcon className="size-4 text-[#209DB3]" />
                    Data Freshness
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
                <Badge variant={badgeVariant} className={`text-xs ${badgeClass}`}>
                    {badgeLabel}
                </Badge>
                {hasSynced && syncStatus?.completed_at && (
                    <p className="text-xs text-muted-foreground">
                        Last sync: {formatRelative(syncStatus.completed_at)}
                    </p>
                )}
                {hasSynced && syncStatus?.records_synced != null && (
                    <p className="text-xs text-muted-foreground">
                        {syncStatus.records_synced.toLocaleString()} records synced
                    </p>
                )}
                {hasSynced && syncStatus?.sync_type && (
                    <p className="text-xs text-muted-foreground capitalize">
                        {syncStatus.sync_type} sync
                    </p>
                )}
                {!hasSynced && (
                    <p className="text-xs text-muted-foreground">
                        No data synced yet. Use "Sync from Xero" above to get started.
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

// QualityChecksCard hidden — checks validate VNext engine, not current actuals surfaces

// ── Model Activity Card ──────────────────────────────────────

function ModelActivityCard({workspaceId, scenarioId}: {workspaceId: string; scenarioId: string}) {
    const {ops, isLoading} = useVNextOps(workspaceId, scenarioId);

    if (isLoading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <ActivityIcon className="size-4 text-[#209DB3]" />
                        Model Activity
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    const activeOps = ops.filter((o) => o.is_active);
    const agentOps = activeOps.filter((o) => o.created_by === "agent");
    const humanOps = activeOps.filter((o) => o.created_by === "human");
    const latestOp = ops.length > 0 ? ops.reduce((a, b) => (a.created_at > b.created_at ? a : b)) : null;

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <ActivityIcon className="size-4 text-[#209DB3]" />
                    Model Activity
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1.5">
                {ops.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No ops yet</p>
                ) : (
                    <>
                        <p className="text-sm">
                            <span className="font-medium">{activeOps.length}</span>
                            <span className="text-muted-foreground text-xs ml-1">active ops</span>
                            <span className="text-muted-foreground text-xs ml-2">
                                ({agentOps.length} agent, {humanOps.length} human)
                            </span>
                        </p>
                        <p className="text-sm">
                            <span className="font-medium">{ops.length}</span>
                            <span className="text-muted-foreground text-xs ml-1">total revisions</span>
                        </p>
                        {latestOp && (
                            <p className="text-xs text-muted-foreground">
                                Latest: {formatRelative(latestOp.created_at)}
                            </p>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

// ── Assumptions Pipeline Card ────────────────────────────────

function AssumptionsPipelineCard({workspaceId}: {workspaceId: string}) {
    const {assumptions, isLoading} = useVNextAssumptions(workspaceId);

    if (isLoading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <ClipboardCheckIcon className="size-4 text-[#209DB3]" />
                        Assumptions Pipeline
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    const proposed = assumptions.filter((a) => a.status === "proposed").length;
    const approved = assumptions.filter((a) => a.status === "approved").length;
    const committed = assumptions.filter((a) => a.status === "committed").length;
    const rejected = assumptions.filter((a) => a.status === "rejected").length;

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <ClipboardCheckIcon className="size-4 text-[#209DB3]" />
                    Assumptions Pipeline
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {assumptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No assumptions yet</p>
                ) : (
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                                {proposed} proposed
                            </Badge>
                            <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                                {approved} approved
                            </Badge>
                            <Badge variant="outline" className="text-xs border-slate-300 text-slate-600">
                                {committed} committed
                            </Badge>
                            {rejected > 0 && (
                                <Badge variant="outline" className="text-xs border-red-300 text-red-600">
                                    {rejected} rejected
                                </Badge>
                            )}
                        </div>
                        {proposed > 0 && (
                            <p className="text-xs text-amber-600 font-medium">
                                {proposed} pending approval{proposed !== 1 ? "s" : ""}
                            </p>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ── Key Metrics Card ─────────────────────────────────────────

function KeyMetricsCard({workspaceId, scenarioId, forecastMonths = 0}: {workspaceId: string; scenarioId: string; forecastMonths?: number}) {
    const {data, isLoading} = useVNextSummary(workspaceId, scenarioId, forecastMonths);

    if (isLoading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DollarSignIcon className="size-4 text-[#209DB3]" />
                        Key Metrics
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    if (!data) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DollarSignIcon className="size-4 text-[#209DB3]" />
                        Key Metrics
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <p className="text-xs text-muted-foreground">No data available — sync from Xero to populate</p>
                </CardContent>
            </Card>
        );
    }

    const revenueKpi = data.kpis.find((k) => k.key === "revenue");
    const costsKpi = data.kpis.find((k) => k.key === "costs");
    const expensesKpi = data.kpis.find((k) => k.key === "expenses");
    const profitKpi = data.kpis.find((k) => k.key === "net");

    // Production Summary is actuals-only while forecast surfaces remain hidden.
    const totalCostsValue =
        (costsKpi ? costsKpi.actual_total : 0) +
        (expensesKpi ? expensesKpi.actual_total : 0);

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <DollarSignIcon className="size-4 text-[#209DB3]" />
                    Key Metrics
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
                <div className="grid grid-cols-3 gap-3">
                    {revenueKpi && (
                        <div>
                            <p className="text-xs text-muted-foreground">Revenue</p>
                            <p className="text-lg font-semibold text-green-700">{formatCurrency(revenueKpi.actual_total)}</p>
                        </div>
                    )}
                    {(costsKpi || expensesKpi) && (
                        <div>
                            <p className="text-xs text-muted-foreground">Total Costs</p>
                            <p className="text-lg font-semibold text-red-700">{formatCurrency(totalCostsValue)}</p>
                        </div>
                    )}
                    {profitKpi && (
                        <div>
                            <p className="text-xs text-muted-foreground">Net Profit</p>
                            <p className={`text-lg font-semibold ${profitKpi.actual_total >= 0 ? "text-green-700" : "text-red-700"}`}>
                                {formatCurrency(profitKpi.actual_total)}
                            </p>
                        </div>
                    )}
                </div>
                <p className="text-xs text-muted-foreground">
                    Actuals only — {data.n_actual_months} months with data
                </p>
            </CardContent>
        </Card>
    );
}

// ── SummaryTab ────────────────────────────────────────────────

export function SummaryTab({workspaceId, scenarioId, forecastMonths = 0}: SummaryTabProps) {
    return (
        <div className="space-y-6 pb-6">
            {/* Info cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <DataFreshnessCard />
                {/* QualityChecksCard hidden — checks validate VNext engine, not current actuals surfaces */}
                <ModelActivityCard workspaceId={workspaceId} scenarioId={scenarioId} />
                <AssumptionsPipelineCard workspaceId={workspaceId} />
                <KeyMetricsCard workspaceId={workspaceId} scenarioId={scenarioId} forecastMonths={forecastMonths} />
            </div>

        </div>
    );
}
